import React from 'react';
import clsx from 'clsx';
import { LoaderContext } from '@shared/contexts';
import Typography from '@material-ui/core/Typography';
import { showToast } from '@shared/components';
import { DEFAULT_ERROR_MESSAGE, TOAST_TYPES } from '@shared/constants';

import BrandLogo from '@assets/icons/brand-logo.png';
import WarningIcon from '@assets/icons/errorIcon.png';
import WarningDarkIcon from '@assets/icons/error-dark.png';

import useStyles from './styles';

let timeout = null;
let timer = null;
const STATUS = {
  NOT_SCHEDULED: 'not-scheduled',
  SCHEDULED: 'scheduled',
  READY: 'ready',
  STARTED: 'started',
};

const noop = () => {};

const defaultState = {
  startTime: null,
  timeLeft: null,
  maintenanceStatus: STATUS.NOT_SCHEDULED,
};

export default ({
  getMaintenanceSlot = noop,
  handleMaintenanceSlotChange = noop,
}) => {
  const { showLoader, hideLoader } = React.useContext(LoaderContext);
  const [state, setState] = React.useState(defaultState);

  const classes = useStyles({
    isReady: state.maintenanceStatus === STATUS.READY,
  });

  const fetchMaintenanceSlot = React.useCallback(
    async (isLoader = false) => {
      !isLoader && showLoader();
      const { data, error } = await getMaintenanceSlot();

      if (error) {
        showToast(DEFAULT_ERROR_MESSAGE, TOAST_TYPES.ERROR);
        !isLoader && hideLoader();
      }

      handleMaintenanceSlotChange(
        data?.maintanence?.start_time
          ? new Date(data?.maintanence?.start_time).getTime() - Date.now()
          : null
      );
      setState(prevState => {
        const isMaintenanceScheduled = !!data?.maintanence?.start_time;
        if (!isMaintenanceScheduled) {
          return {
            ...prevState,
            ...defaultState,
          };
        }
        return {
          ...prevState,
          startTime: data?.maintanence?.start_time,
        };
      });

      !isLoader && hideLoader();
    },
    [getMaintenanceSlot]
  );

  const subscribeMaintance = (isLoader = false) => {
    fetchMaintenanceSlot(isLoader);
    clearTimeout(timeout);
    timeout = setTimeout(() => subscribeMaintance(true), 1000 * 120);
  };

  React.useEffect(() => {
    subscribeMaintance();
    return () => clearTimeout(timeout);
  }, [fetchMaintenanceSlot]);

  const formatTime = ms => {
    const padNumber = num => ('000' + num).slice(2 * -1);
    let days, hours, minutes, seconds;
    seconds = Math.floor(ms / 1000);
    minutes = Math.floor(seconds / 60);
    seconds = seconds % 60;
    hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
    days = Math.floor(hours / 24);
    hours = hours % 24;

    const dayString = `${!!days ? days + (days > 1 ? ' days' : ' day') : ''}`;

    return `${dayString} ${padNumber(hours)}:${padNumber(minutes)}:${padNumber(
      seconds
    )}`;
  };

  const subscribeTimer = () => {
    const timeStamp = new Date(state.startTime).getTime() - Date.now();
    const timeInSeconds = parseFloat(timeStamp / 1000).toFixed(3);
    const isTimeElapsed = timeInSeconds < 0;

    setState(prevState => ({
      ...prevState,
      timeLeft: isTimeElapsed ? null : formatTime(timeStamp),
      maintenanceStatus: isTimeElapsed
        ? STATUS.STARTED
        : timeInSeconds / 60 < 5
        ? STATUS.READY
        : STATUS.SCHEDULED,
    }));

    clearTimeout(timer);
    if (!isTimeElapsed) {
      timer = setTimeout(subscribeTimer, 1000 * 1);
    }
  };

  React.useEffect(() => {
    if (state.startTime) {
      subscribeTimer();
    }
    return () => clearTimeout(timer);
  }, [state.startTime]);

  if (state.maintenanceStatus === STATUS.NOT_SCHEDULED) {
    return <React.Fragment />;
  }

  return (
    <React.Fragment>
      {(state.maintenanceStatus === STATUS.SCHEDULED ||
        state.maintenanceStatus === STATUS.READY) && (
        <div className={clsx('align-center', classes.maintenanceWarning)}>
          <img
            className={clsx('mr-2 p-relative', classes.warningIcon)}
            src={
              state.maintenanceStatus === STATUS.READY
                ? WarningIcon
                : WarningDarkIcon
            }
          />
          <Typography
            variant='body'
            className={clsx({
              'color-white': state.maintenanceStatus === STATUS.READY,
            })}
          >
            Scheduled maintenance will begin in {state.timeLeft}
          </Typography>
        </div>
      )}
      {state.maintenanceStatus === STATUS.STARTED && (
        <div
          className={clsx(
            classes.maintenanceBanner,
            'd-flex f-align-center f-justify-center p-fixed'
          )}
        >
          <div className='align-center'>
            <img src={BrandLogo} />
            <Typography variant='h5' className='color-white mt-10 mb-8'>
              Conductor is undergoing scheduled maintenance.
            </Typography>
            <Typography variant='h5' className='color-white'>
              Please try to login after the maintenance has been <br />
              completed.
            </Typography>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};
